import { loadLayerSettings } from "../../components/Map/mapStatePersistence";

export type LngLat = [number, number]; // [lng, lat]

export interface NavGraph {
  version: number;
  quantScale: number;
  nodes: LngLat[];
  adjIndex: number[];
  adjTo: number[];
  adjW: number[]; // meters
  adjD?: (number | null)[]; // depth in meters (null if unknown) - version 2+
  adjF?: number[]; // feature index for each adjacency - version 3+
  adjH?: (number | null)[]; // bridge clearance in meters (null if unknown) - version 3+
}

export interface SegmentRecord {
  u: number;
  v: number;
  a: LngLat;
  b: LngLat;
  w: number; // meters
  d?: number | null; // depth in meters (null if unknown) - version 2+
  f?: number; // feature index - version 3+
  h?: number | null; // bridge clearance in meters (null if unknown) - version 3+
}

export interface SegmentIndex {
  version: number;
  cellSizeDeg: number;
  origin: LngLat; // [lng0, lat0]
  bbox: [number, number, number, number]; // [minLng,minLat,maxLng,maxLat]
  grid: Record<string, number[]>; // "cx,cy" -> segIds
  segments: SegmentRecord[];
  scoutFeatureIndices?: number[]; // indices of scout route features - version 3+
}

export interface SnapResult {
  snapped: LngLat;
  segmentId: number;
  segment: SegmentRecord;
  t: number; // 0..1 along segment a->b
  distanceMetersApprox: number;
}

export interface RouteResult {
  start: SnapResult;
  end: SnapResult;
  distanceMeters: number;
  line: GeoJSON.Feature<GeoJSON.LineString>;
  points: GeoJSON.FeatureCollection<GeoJSON.Point>;
}

export interface FairwayRoutingOptions {
  useScoutRoutes?: boolean;
}

let graphPromise: Promise<NavGraph> | null = null;
let indexPromise: Promise<SegmentIndex> | null = null;
let loadingState: {
  isLoading: boolean;
  isLoaded: boolean;
  error: Error | null;
} = {
  isLoading: false,
  isLoaded: false,
  error: null,
};

// Listeners for loading state changes (for React components)
type LoadingStateListener = (state: {
  isLoading: boolean;
  isLoaded: boolean;
  error: Error | null;
}) => void;
const loadingStateListeners = new Set<LoadingStateListener>();

function notifyLoadingStateListeners(): void {
  const state = { ...loadingState };
  loadingStateListeners.forEach((listener) => listener(state));
}

async function fetchJson<T>(url: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(`Fairway routing: ${msg} (${url})`);
  }
  if (!res.ok) throw new Error(`Fairway routing: ${url} → ${res.status}`);
  return (await res.json()) as T;
}

function getCdnUrl(path: string): string {
  const cdnUrl = import.meta.env.VITE_CDN_URL;
  if (!cdnUrl) {
    throw new Error("VITE_CDN_URL is not defined");
  }
  // Ensure no double slashes
  const base = cdnUrl.endsWith("/") ? cdnUrl.slice(0, -1) : cdnUrl;
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${base}${cleanPath}`;
}

/**
 * Start loading fairway data in the background (non-blocking).
 * This should be called during app initialization.
 */
export function startLoadingFairwayData(): void {
  if (graphPromise || indexPromise) {
    // Already loading or loaded
    return;
  }

  loadingState = { isLoading: true, isLoaded: false, error: null };
  notifyLoadingStateListeners();

  const graphUrl = getCdnUrl("/rgraph/navgraph-v7.json.gz");
  const indexUrl = getCdnUrl("/rgraph/segments-v7.index.json.gz");

  graphPromise = fetchJson<NavGraph>(graphUrl).catch((err) => {
    loadingState.error = err instanceof Error ? err : new Error(String(err));
    throw err;
  });

  indexPromise = fetchJson<SegmentIndex>(indexUrl).catch((err) => {
    loadingState.error = err instanceof Error ? err : new Error(String(err));
    throw err;
  });

  Promise.all([graphPromise, indexPromise])
    .then(() => {
      loadingState = { isLoading: false, isLoaded: true, error: null };
      notifyLoadingStateListeners();
    })
    .catch((err) => {
      loadingState = {
        isLoading: false,
        isLoaded: false,
        error: err instanceof Error ? err : new Error(String(err)),
      };
      notifyLoadingStateListeners();
    });
}

/**
 * Get the current loading state of fairway data.
 */
export function getFairwayDataLoadingState(): {
  isLoading: boolean;
  isLoaded: boolean;
  error: Error | null;
} {
  return { ...loadingState };
}

export async function loadFairwayData(): Promise<{
  graph: NavGraph;
  index: SegmentIndex;
}> {
  // If not already loading, start loading now (fallback for direct calls)
  if (!graphPromise || !indexPromise) {
    startLoadingFairwayData();
  }

  if (!graphPromise || !indexPromise) {
    throw new Error("Failed to initialize fairway data loading");
  }

  const [graph, index] = await Promise.all([graphPromise, indexPromise]);
  return { graph, index };
}

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

function haversineMeters(a: LngLat, b: LngLat): number {
  const R = 6371000;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

type XY = { x: number; y: number };

function lngLatToXY(p: LngLat, lat0Rad: number): XY {
  // Equirectangular projection around lat0 for local computations (meters-ish).
  const R = 6371000;
  const x = R * toRad(p[0]) * Math.cos(lat0Rad);
  const y = R * toRad(p[1]);
  return { x, y };
}

function xyToLngLat(p: XY, lat0Rad: number): LngLat {
  const R = 6371000;
  const lat = (p.y / R) * (180 / Math.PI);
  const lng = (p.x / (R * Math.cos(lat0Rad))) * (180 / Math.PI);
  return [lng, lat];
}

function closestPointOnSegment(
  p: LngLat,
  a: LngLat,
  b: LngLat,
): { q: LngLat; t: number; distApprox: number } {
  const lat0Rad = toRad(p[1]);
  const pxy = lngLatToXY(p, lat0Rad);
  const axy = lngLatToXY(a, lat0Rad);
  const bxy = lngLatToXY(b, lat0Rad);

  const abx = bxy.x - axy.x;
  const aby = bxy.y - axy.y;
  const apx = pxy.x - axy.x;
  const apy = pxy.y - axy.y;
  const ab2 = abx * abx + aby * aby;
  const t =
    ab2 === 0 ? 0 : Math.max(0, Math.min(1, (apx * abx + apy * aby) / ab2));
  const qxy = { x: axy.x + t * abx, y: axy.y + t * aby };
  const dx = pxy.x - qxy.x;
  const dy = pxy.y - qxy.y;
  const distApprox = Math.sqrt(dx * dx + dy * dy);
  return { q: xyToLngLat(qxy, lat0Rad), t, distApprox };
}

export function snapToFairway(
  point: LngLat,
  index: SegmentIndex,
  options?: {
    allowScoutRoutes?: boolean;
    scoutFeatureSet?: Set<number> | null;
  },
): SnapResult {
  const scoutFeatureSet = options?.scoutFeatureSet ?? getScoutFeatureSet(index);
  const allowScoutRoutes = options?.allowScoutRoutes ?? true;
  return snapToFairwayWithOptions(point, index, {
    allowScoutRoutes,
    scoutFeatureSet,
  });
}

function snapToFairwayWithOptions(
  point: LngLat,
  index: SegmentIndex,
  options: {
    allowScoutRoutes: boolean;
    scoutFeatureSet: Set<number> | null;
  },
): SnapResult {
  const { allowScoutRoutes, scoutFeatureSet } = options;
  const [lng0, lat0] = index.origin;
  const cs = index.cellSizeDeg;
  const cx0 = Math.floor((point[0] - lng0) / cs);
  const cy0 = Math.floor((point[1] - lat0) / cs);

  let best: SnapResult | null = null;

  // Search expanding square rings.
  const MAX_R = 8; // 17x17 cells max; still fast for this dataset
  for (let r = 0; r <= MAX_R; r++) {
    for (let dx = -r; dx <= r; dx++) {
      for (let dy = -r; dy <= r; dy++) {
        if (r !== 0 && Math.abs(dx) !== r && Math.abs(dy) !== r) continue; // ring only
        const k = `${cx0 + dx},${cy0 + dy}`;
        const segIds = index.grid[k];
        if (!segIds) continue;

        for (const segId of segIds) {
          const seg = index.segments[segId];
          if (
            !allowScoutRoutes &&
            scoutFeatureSet &&
            typeof seg.f === "number" &&
            scoutFeatureSet.has(seg.f)
          ) {
            continue;
          }
          const { q, t, distApprox } = closestPointOnSegment(
            point,
            seg.a,
            seg.b,
          );
          if (!best || distApprox < best.distanceMetersApprox) {
            best = {
              snapped: q,
              segmentId: segId,
              segment: seg,
              t,
              distanceMetersApprox: distApprox,
            };
          }
        }
      }
    }

    // Early exit if very close.
    const currentBest = best;
    if (currentBest !== null && currentBest.distanceMetersApprox < 25) break;
  }

  if (!best) {
    throw new Error("No fairway segment found near click");
  }

  return best;
}

function getScoutFeatureSet(index: SegmentIndex): Set<number> | null {
  if (!Array.isArray(index.scoutFeatureIndices)) return null;
  const set = new Set<number>();
  for (const entry of index.scoutFeatureIndices) {
    if (typeof entry === "number" && Number.isFinite(entry)) {
      set.add(entry);
    }
  }
  return set.size > 0 ? set : null;
}

function resolveScoutRoutingSetting(options?: FairwayRoutingOptions): boolean {
  if (typeof options?.useScoutRoutes === "boolean") {
    return options.useScoutRoutes;
  }
  try {
    const settings = loadLayerSettings();
    return Boolean(settings.scoutRoutes && settings.scoutRouting);
  } catch {
    return false;
  }
}

class MinHeap {
  private heap: Array<{ id: number; f: number }> = [];
  push(item: { id: number; f: number }) {
    this.heap.push(item);
    this.bubbleUp(this.heap.length - 1);
  }
  pop(): { id: number; f: number } | undefined {
    if (this.heap.length === 0) return undefined;
    const top = this.heap[0];
    const last = this.heap.pop()!;
    if (this.heap.length > 0) {
      this.heap[0] = last;
      this.bubbleDown(0);
    }
    return top;
  }
  get size() {
    return this.heap.length;
  }
  private bubbleUp(i: number) {
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this.heap[p].f <= this.heap[i].f) break;
      [this.heap[p], this.heap[i]] = [this.heap[i], this.heap[p]];
      i = p;
    }
  }
  private bubbleDown(i: number) {
    const n = this.heap.length;
    while (true) {
      const l = i * 2 + 1;
      const r = l + 1;
      let m = i;
      if (l < n && this.heap[l].f < this.heap[m].f) m = l;
      if (r < n && this.heap[r].f < this.heap[m].f) m = r;
      if (m === i) break;
      [this.heap[m], this.heap[i]] = [this.heap[i], this.heap[m]];
      i = m;
    }
  }
}

export function computeFairwayRouteSync(
  graph: NavGraph,
  index: SegmentIndex,
  startClick: LngLat,
  endClick: LngLat,
  vesselDraft: number = 0, // meters, 0 means no filtering
  vesselHeight: number = 0, // meters, 0 means no filtering
  options?: FairwayRoutingOptions,
): RouteResult {
  const allowScoutRoutes = options?.useScoutRoutes !== false;
  const scoutFeatureSet = getScoutFeatureSet(index);
  const snapOptions = {
    allowScoutRoutes,
    scoutFeatureSet,
  };
  const start = snapToFairwayWithOptions(startClick, index, snapOptions);
  const end = snapToFairwayWithOptions(endClick, index, snapOptions);

  // If clicks are extremely far from the network, fail fast.
  const MAX_SNAP_METERS = 2000;
  if (start.distanceMetersApprox > MAX_SNAP_METERS)
    throw new Error("Start point too far from fairways");
  if (end.distanceMetersApprox > MAX_SNAP_METERS)
    throw new Error("End point too far from fairways");

  const N = graph.nodes.length;
  const START = N;
  const GOAL = N + 1;

  const startCoord = start.snapped;
  const endCoord = end.snapped;

  const startU = start.segment.u;
  const startV = start.segment.v;
  const endU = end.segment.u;
  const endV = end.segment.v;

  const startCostU = start.segment.w * start.t;
  const startCostV = start.segment.w * (1 - start.t);
  const endCostU = end.segment.w * end.t;
  const endCostV = end.segment.w * (1 - end.t);

  const directSameSegment =
    start.segmentId === end.segmentId
      ? start.segment.w * Math.abs(start.t - end.t)
      : null;

  const totalNodes = N + 2;
  const g = new Array<number>(totalNodes).fill(Infinity);
  const cameFrom = new Array<number>(totalNodes).fill(-1);
  const closed = new Array<boolean>(totalNodes).fill(false);

  const coordOf = (id: number): LngLat => {
    if (id < N) return graph.nodes[id];
    if (id === START) return startCoord;
    return endCoord;
  };

  const heuristic = (id: number): number =>
    haversineMeters(coordOf(id), endCoord);

  const open = new MinHeap();
  g[START] = 0;
  open.push({ id: START, f: heuristic(START) });

  const neighbors = (id: number, cb: (to: number, w: number) => void) => {
    if (id < N) {
      // base graph neighbors
      const begin = graph.adjIndex[id];
      const endIdx = graph.adjIndex[id + 1];
      const adjD = graph.adjD; // depth array (may be undefined for old graphs)
      const adjF = graph.adjF;
      const adjH = graph.adjH;
      for (let i = begin; i < endIdx; i++) {
        if (
          !allowScoutRoutes &&
          scoutFeatureSet &&
          adjF &&
          typeof adjF[i] === "number" &&
          scoutFeatureSet.has(adjF[i])
        ) {
          continue;
        }
        // Filter by depth if vessel draft is specified
        if (vesselDraft > 0 && adjD) {
          const depth = adjD[i];
          // Skip edge if depth is known and less than vessel draft
          if (depth !== null && depth !== undefined && depth < vesselDraft) {
            continue;
          }
        }
        // Filter by bridge clearance if vessel height is specified
        if (vesselHeight > 0 && adjH) {
          const clearance = adjH[i];
          if (
            clearance !== null &&
            clearance !== undefined &&
            clearance < vesselHeight
          ) {
            continue;
          }
        }
        cb(graph.adjTo[i], graph.adjW[i]);
      }

      // connect to temporary nodes if this node is an endpoint
      if (id === startU) cb(START, startCostU);
      if (id === startV) cb(START, startCostV);
      if (id === endU) cb(GOAL, endCostU);
      if (id === endV) cb(GOAL, endCostV);
      return;
    }

    if (id === START) {
      cb(startU, startCostU);
      cb(startV, startCostV);
      if (directSameSegment !== null) cb(GOAL, directSameSegment);
      return;
    }

    // id === GOAL
    cb(endU, endCostU);
    cb(endV, endCostV);
    if (directSameSegment !== null) cb(START, directSameSegment);
  };

  while (open.size) {
    const cur = open.pop()!;
    const current = cur.id;
    if (closed[current]) continue;
    if (current === GOAL) break;
    closed[current] = true;

    neighbors(current, (to, w) => {
      if (closed[to]) return;
      const tentative = g[current] + w;
      if (tentative < g[to]) {
        g[to] = tentative;
        cameFrom[to] = current;
        open.push({ id: to, f: tentative + heuristic(to) });
      }
    });
  }

  if (!Number.isFinite(g[GOAL])) {
    throw new Error("No fairway path found");
  }

  // Reconstruct node path
  const pathIds: number[] = [];
  let cur = GOAL;
  while (cur !== -1) {
    pathIds.push(cur);
    if (cur === START) break;
    cur = cameFrom[cur];
  }
  pathIds.reverse();

  // Ensure START is at beginning (it should be).
  if (pathIds[0] !== START) pathIds.unshift(START);
  if (pathIds[pathIds.length - 1] !== GOAL) pathIds.push(GOAL);

  const coords: LngLat[] = [];
  for (const id of pathIds) {
    const c = coordOf(id);
    const prev = coords[coords.length - 1];
    if (!prev || prev[0] !== c[0] || prev[1] !== c[1]) coords.push(c);
  }

  // Build full coordinate sequence including off-network legs from the actual
  // click locations to the snapped fairway entry/exit points.
  const fullCoords: LngLat[] = [];

  // Pre-segment: from actual start click to snapped start on fairway
  if (startClick[0] !== startCoord[0] || startClick[1] !== startCoord[1]) {
    fullCoords.push(startClick, startCoord);
  } else {
    fullCoords.push(startCoord);
  }

  // Main fairway path (avoid duplicates)
  for (const c of coords) {
    const prev = fullCoords[fullCoords.length - 1];
    if (!prev || prev[0] !== c[0] || prev[1] !== c[1]) {
      fullCoords.push(c);
    }
  }

  // Post-segment: from snapped end on fairway to actual end click
  if (endClick[0] !== endCoord[0] || endClick[1] !== endCoord[1]) {
    const last = fullCoords[fullCoords.length - 1];
    if (!last || last[0] !== endCoord[0] || last[1] !== endCoord[1]) {
      fullCoords.push(endCoord);
    }
    fullCoords.push(endClick);
  }

  // Total distance = network distance + pre/post straight-line segments.
  let totalDistance = g[GOAL];
  if (startClick[0] !== startCoord[0] || startClick[1] !== startCoord[1]) {
    totalDistance += haversineMeters(startClick, startCoord);
  }
  if (endClick[0] !== endCoord[0] || endClick[1] !== endCoord[1]) {
    totalDistance += haversineMeters(endCoord, endClick);
  }

  const line: GeoJSON.Feature<GeoJSON.LineString> = {
    type: "Feature",
    properties: { distanceMeters: totalDistance },
    geometry: { type: "LineString", coordinates: fullCoords },
  };

  const points: GeoJSON.FeatureCollection<GeoJSON.Point> = {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: { role: "start" },
        geometry: { type: "Point", coordinates: startCoord },
      },
      {
        type: "Feature",
        properties: { role: "end" },
        geometry: { type: "Point", coordinates: endCoord },
      },
    ],
  };

  return {
    start,
    end,
    distanceMeters: totalDistance,
    line,
    points,
  };
}

export async function computeFairwayRoute(
  startClick: LngLat,
  endClick: LngLat,
  vesselDraft: number = 0, // meters, 0 means no filtering
  vesselHeight: number = 0, // meters, 0 means no filtering
  options?: FairwayRoutingOptions,
): Promise<RouteResult> {
  const { graph, index } = await loadFairwayData();
  const useScoutRoutes = resolveScoutRoutingSetting(options);
  return computeFairwayRouteSync(
    graph,
    index,
    startClick,
    endClick,
    vesselDraft,
    vesselHeight,
    { useScoutRoutes },
  );
}
